// src/components/TyroPayment.js

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
export const defaultSettings = {
  paySecret: '',
  theme: 'default',
  styleProps: {
    bodyBackgroundColor: '#fff',
  },
  options: {
    applePay: {
      enabled: true,
      totalLabel: 'QR Order Payment',
      supportedNetworks: ['visa', 'mastercard', 'amex', 'jcb', 'maestro']
    },
    googlePay: {
      enabled: true,
      merchantInfo: {
        merchantName: "Intelligent POS",
        merchantId: "BCR2DN4T4SRKVMCW"
      }
    },
    creditCardForm: {
      enabled: true
    }
  },
};

const TyroPayment = ({ paySecret, onPaymentSuccess, onPaymentError }) => {
  const tyroJSRef = useRef({}); // Used to store tyroJSInstance

  const [configuration] = useState(defaultSettings);
  const [error, setError] = useState({});
  const [loading, setLoading] = useState(false);
  const [libraryReady, setLibraryReady] = useState(false);
  const [payRequestReady, setPayRequestReady] = useState(false);
  const [payFormReady, setPayFormReady] = useState(false);
  const [submittingOverlay, setSubmittingOverlay] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [payComplete, setPayComplete] = useState(false);
  const [formValid, setFormValid] = useState(false);

  const { theme, styleProps, options } = configuration;

  // STEP 1: Check if Tyro.js script is loaded
  useEffect(() => {
    if (window.Tyro) {
      setLibraryReady(true);
    } else {
      // If the script is not loaded yet, add listener or polling
      const checkInterval = setInterval(() => {
        if (window.Tyro) {
          setLibraryReady(true);
          clearInterval(checkInterval);
        }
      }, 100);

      // Clear timer to prevent component unmount issues
      return () => clearInterval(checkInterval);
    }
  }, []);

  // STEP 2: Initialize Tyro.js using Pay Secret
  useEffect(() => {
    if (libraryReady && paySecret) {
      initPayRequest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryReady, paySecret]);

  const initPayRequest = async () => {
    setLoading(true);
    setPayRequestReady(false);
    setPayFormReady(false);
    setSubmitting(false);
    setError({});
    
    try {
    
      tyroJSRef.current.tyroJSInstance = window.Tyro({
        liveMode: true, // Set to true for production
      });
      
      await tyroJSRef.current.tyroJSInstance.init(paySecret);
      
      const payFormElement = document.getElementById('tyro-pay-form');
      if (payFormElement) {
        payFormElement.innerHTML = '';
      }
      setPayRequestReady(true);
    } catch (err) {
      console.error('Error initializing Tyro:', err);
      setError({
        type: 'critical',
        errorCode: err.errorCode || 'INIT_ERROR',
        message: err.message || err.toString(),
        debug: { err },
      });
      onPaymentError(err);
      setLoading(false);
    }
  };

  // STEP 3: Embed payment form into DOM
  useEffect(() => {
    if (libraryReady && payRequestReady && !payFormReady) {
      initPayForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payRequestReady]);

  const initPayForm = async () => {
    setPayFormReady(false);
    setSubmitting(false);
    try {
      const payFormElement = document.getElementById('tyro-pay-form');
      if (!payFormElement) {
        throw new Error('Pay Form is not mounted');
      }
      payFormElement.innerHTML = '';
      const payForm = tyroJSRef.current.tyroJSInstance.createPayForm({
        theme,
        styleProps,
        options,
      });
      
      // Bind wallet payment listeners
      payForm.setWalletPaymentBeginListener((paymentType) => {
        console.log(`Wallet payment started: ${paymentType}`);
        setSubmittingOverlay(true);
        if(paymentType === 'APPLE_PAY') {
          console.log('Apple Pay payment initiated');
        } else if(paymentType === 'GOOGLE_PAY'){
          console.log('Google Pay payment initiated');
        }
      });
      
      payForm.setWalletPaymentCancelledListener((paymentType) => {
        console.log(`Wallet payment cancelled: ${paymentType}`);
        setSubmittingOverlay(false);
        if(paymentType === 'APPLE_PAY') {
          console.log('Apple Pay payment cancelled');
        } else if(paymentType === 'GOOGLE_PAY'){
          console.log('Google Pay payment cancelled');
        }
      });
      
      payForm.setWalletPaymentCompleteListener(async (paymentType, error) => {
        console.log(`Wallet payment complete: ${paymentType}`, error);
        
        if (error) {
          const { type, errorMessage, errorCode, gatewayCode } = error;
          console.error('Wallet payment error:', { type, errorMessage, errorCode, gatewayCode });
          
          let formattedError;
          switch (type) {
            case "SERVER_VALIDATION_ERROR":
              formattedError = {
                type: 'validation',
                errorCode: errorCode || 'VALIDATION_ERROR',
                message: errorMessage || 'Server validation failed',
                debug: { error, gatewayCode }
              };
              break;
            case "CARD_ERROR":
              formattedError = {
                type: 'card',
                errorCode: errorCode || 'CARD_ERROR',
                message: errorMessage || 'Card payment failed',
                debug: { error, gatewayCode }
              };
              break;
            case "SERVER_ERROR":
              formattedError = {
                type: 'server',
                errorCode: errorCode || 'SERVER_ERROR',
                message: errorMessage || 'Server error occurred',
                debug: { error, gatewayCode }
              };
              break;
            default:
              formattedError = {
                type: 'critical',
                errorCode: errorCode || 'UNKNOWN_ERROR',
                message: errorMessage || error.toString(),
                debug: { error, gatewayCode }
              };
          }
          
          setError(formattedError);
          onPaymentError(formattedError);
          setSubmittingOverlay(false);
        } else {
          // Payment completed successfully, fetch the payment result
          try {
            await getPaymentResult();
          } catch (err) {
            console.error('Error fetching payment result after wallet payment:', err);
            setError({
              type: 'critical',
              errorCode: err.errorCode || 'FETCH_ERROR',
              message: 'Failed to retrieve payment result',
              debug: { err }
            });
            onPaymentError(err);
            setSubmittingOverlay(false);
          }
        }
      });
      
      // Try to use Tyro's form validation listener if available
      try {
        if (typeof payForm.setFormValidListener === 'function') {
          payForm.setFormValidListener((isValid) => {
            console.log("Form validation from Tyro API:", isValid);
            setFormValid(isValid);
          });
        }
      } catch (e) {
        console.log("Tyro FormValidListener not available, using DOM monitoring");
      }
      
      payForm.setReadyListener(() => {
        setPayFormReady(true);
        setLoading(false);
        
        // Wait a bit for iframe to be fully initialized
        setTimeout(() => {
          setupFormValidation();
        }, 1000);
      });
      
      payForm.inject('#tyro-pay-form');
    } catch (err) {
      setError({
        type: 'critical',
        errorCode: err.errorCode,
        message: err.toString(),
        debug: { err },
      });
      onPaymentError(err);
      setLoading(false);
    }
  };

  // Setup form validation by monitoring changes in the form
  const setupFormValidation = () => {
    // First check for iframe content
    const payFormElement = document.getElementById('tyro-pay-form');
    if (!payFormElement) return;
    
    const iframes = payFormElement.querySelectorAll('iframe');
    if (iframes.length === 0) {
      console.log("No iframe found in payForm");
      // Set true for testing only - should be removed in production
      setFormValid(true);
      return;
    }
    
    // Manual button activation after a delay to ensure form is loaded
    // This is a fallback if we can't access iframe content
    setTimeout(() => {
      console.log("Activating button after timeout");
      setFormValid(true);
    }, 3000);
    
    // Set up an interval to check payment button status from Tyro forms
    const validationInterval = setInterval(() => {
      try {
        // Look for any disabled submit buttons in the Tyro payment form area
        const tyroSubmitBtn = document.querySelector('#tyro-pay-form button[type="submit"]:not(:disabled)');
        if (tyroSubmitBtn) {
          console.log("Found enabled Tyro submit button");
          setFormValid(true);
          clearInterval(validationInterval);
        }
        
        // Also check for data completeness by inspecting container classes
        const completedFields = document.querySelectorAll('#tyro-pay-form .tyro-field--complete, #tyro-pay-form .is-complete');
        if (completedFields && completedFields.length >= 4) {  // Typically need 4 fields: card, name, expiry, cvv
          console.log("Found completed field indicators");
          setFormValid(true);
          clearInterval(validationInterval);
        }
      } catch (e) {
        console.error("Error checking validation status:", e);
      }
    }, 500);
    
    // Clean up interval on component unmount
    return () => {
      clearInterval(validationInterval);
    };
  };

  // STEP 4: Handle payment submission
  const submitPayForm = async (event) => {
    event.preventDefault();
    setError({});
    setSubmitting(true);
    setSubmittingOverlay(true);
    try {
      await tyroJSRef.current.tyroJSInstance.submitPay();
      await getPaymentResult();
      setSubmitting(false);
    } catch (err) {
      if (err?.type === 'CLIENT_VALIDATION_ERROR' && !err?.errorCode) {
        // Ignore client validation errors as they are handled by validation
      } else {
        setError({
          type: 'critical',
          errorCode: err.errorCode,
          message: err.toString(),
          debug: { err },
        });
      }
      setSubmitting(false);
      setSubmittingOverlay(false);
    }
  };

  // STEP 5: Get payment result
  const getPaymentResult = async () => {
    try {
      console.log('Fetching payment result...');
      const payRequest = await tyroJSRef.current.tyroJSInstance.fetchPayRequest();
      console.log('Payment request result:', payRequest);
      
      if (payRequest.status === 'SUCCESS') {
        console.log('Payment successful:', payRequest);

        // // Send to backend to confirm payment status
        // const confirmationResponse = await axios.post('/confirm-tyro-payment', {
        //   payRequestId: payRequest.id,
        //   paySecret: paySecret,
        //   status: payRequest.status,
        //   orderId: payRequest.origin?.orderId,
        // });

        // if (!confirmationResponse.ok) {
        //   throw new Error('Payment confirmation request failed');
        // }

        // const confirmationResult = await confirmationResponse.json();
        // console.log('Backend confirmation result:', confirmationResult);

        // // Handle based on backend confirmation result
        // if (confirmationResult.success && confirmationResult.paymentConfirmed) {

        setPayComplete(true);
        onPaymentSuccess(payRequest);
        
      } else if (payRequest.status === 'FAILED') {
        console.error('Payment failed:', payRequest);
        setError({
          type: 'payment_failed',
          errorCode: payRequest.errorCode || 'PAYMENT_FAILED',
          message: `Payment failed: ${payRequest.status}`,
          debug: { payRequest },
        });
        onPaymentError(payRequest);
        
      } else {
        console.warn('Unexpected payment status:', payRequest.status);
        setError({
          type: 'invalid',
          errorCode: payRequest.errorCode || 'UNKNOWN_STATUS',
          message: `Unexpected payment status: ${payRequest.status}`,
          debug: { payRequest },
        });
        onPaymentError(payRequest);
      }
      
      setSubmittingOverlay(false);
    } catch (err) {
      console.error('Error fetching payment result:', err);
      setError({
        type: 'critical',
        errorCode: err.errorCode || 'FETCH_ERROR',
        message: err.message || err.toString(),
        debug: { err },
      });
      onPaymentError(err);
      setSubmittingOverlay(false);
    }
  };

  // Add loading state styles
  const loadingSpinnerStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px'
  };

  // Optimize error display component
  const ErrorMessage = ({ error }) => (
    <div 
      style={{ 
        padding: '10px', 
        marginBottom: '15px', 
        backgroundColor: '#ffebee', 
        border: '1px solid #ffcdd2',
        borderRadius: '4px',
        color: '#c62828'
      }}
    >
      <p style={{ margin: 0 }}>
        <strong>Error code:</strong> {error.errorCode ?? 'UNKNOWN_ERROR'}
      </p>
      <p style={{ margin: '5px 0 0 0' }}>
        <strong>Error message:</strong> {error.message}
      </p>
    </div>
  );

  return (
    <div style={{ position: 'relative', minHeight: '200px' }}>
      {/* Loading state */}
      {loading && (
        <div style={loadingSpinnerStyle}>
          <div className="spinner"></div>
        </div>
      )}

      {/* Error message display */}
      {error.type && <ErrorMessage error={error} />}

      {/* Payment complete message */}
      {payComplete ? (
        <div style={{ 
          color: '#2e7d32',
          padding: '20px',
          textAlign: 'center',
          backgroundColor: '#e8f5e9',
          borderRadius: '4px',
          marginBottom: '15px'
        }}>
          <svg 
            viewBox="0 0 24 24" 
            style={{ width: '48px', height: '48px', margin: '0 auto 10px' }}
          >
            <path 
              fill="#2e7d32" 
              d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
            />
          </svg>
          <p style={{ fontSize: '18px', margin: 0 }}>Payment completed</p>
        </div>
      ) : (
        <form
          id="pay-form"
          onSubmit={submitPayForm}
          style={{
            pointerEvents: submittingOverlay ? 'none' : 'auto',
            opacity: submittingOverlay ? 0.5 : 1,
          }}
        >
          <div id="tyro-pay-form"></div>
          {payRequestReady && payFormReady && (
            <button
              id="pay-form-submit"
              type="submit"
              disabled={submitting || !formValid}
              style={{
                width: '100%',
                marginTop: '15px',
                padding: '12px 20px',
                backgroundColor: formValid ? '#1976d2' : '#cccccc',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: (submitting || !formValid) ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                transition: 'background-color 0.3s'
              }}
            >
              {submitting ? 'Processing...' : 'Confirm Payment'}
            </button>
          )}
        </form>
      )}

      {/* Submission overlay */}
      {submittingOverlay && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div className="spinner"></div>
          <p style={{ 
            color: '#fff', 
            marginTop: '15px',
            fontSize: '16px'
          }}>
            Processing your payment...
          </p>
        </div>
      )}

      <style>
        {`
          .spinner {
            width: 40px;
            height: 40px;
            border: 4px solid rgba(255, 255, 255, 0.3);
            border-top: 4px solid #ffffff;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }

          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default TyroPayment;
